import secondaryComponents from "../../../../components/secondaryComponents";
import primaryComponents from "../../../../components/primaryComponents";
import { motion } from "framer-motion";
import styles from "./Vatlogs.module.scss";
import { formatCurrency } from "../../../../helpers";
import globalStyles from "../../../../assets/styles/base/globalPage.module.scss";
import { useVatLogs } from "./useVatLogs";
import {
  ToolTipIcon,
  DownloadStatementIcon,
} from "../../../../assets/svgIcons";
import React from "react";

const VatLogsPage = () => {
  const {
    startDate,
    endDate,
    vatSummaryData,
    isFetchingData,
    storeList,
    storeHasMultipleCurrency,
    currentStore,
    selectedTableTab,
    tableTab,
    vatInflowData,
    vatInflowMetaData,
    isLoadingVatInflowData,
    vatOutflowData,
    vatOutflowMetaData,
    isLoadingvatOutflowData,
    isVatLogsExport,
    isChargeVatFilter,
    setSelectedTableTab,
    setCurrentStore,
    filterByStoreId,
    setDateFilter,
    gotoPage,
    setIsVatLogsExport,
    filterIsVatCharged,
  } = useVatLogs();
  let currencyCode = storeList?.find(
    (storeItem) => `${storeItem?.id}` === `${currentStore}`,
  )?.currency_code;

  return (
    <secondaryComponents.MetaSetter
      title="Lumi Merchant | Inventory - VAT Ledger"
      description="View VAT Ledger for your inventory"
    >
      <secondaryComponents.LoaderHelper classNames="mt-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`${styles["inventory-vatlogs"]} ${globalStyles.pageColumnGaps}`}
        >
          <div>
            <h4 className="mb-0">Value Added Tax (VAT)</h4>
            <p>
              Track all VAT paid to supplier and VAT collected from customers in
              one place
            </p>
          </div>
          <div className={styles["inventory-vatlogs__header"]}>
            <div style={{ width: "200px" }}>
              <primaryComponents.SelectField
                options={[
                  { id: "", name: "VAT Charged?" },
                  { id: true, name: "Yes" },
                  { id: false, name: "No" },
                ]}
                idKey="id"
                nameKey="name"
                onChange={(value) => {
                  filterIsVatCharged(value);
                }}
                value={isChargeVatFilter}
              />
            </div>
            <div style={{ width: "200px" }}>
              <primaryComponents.SelectField
                options={
                  storeHasMultipleCurrency
                    ? [...storeList]
                    : [
                        {
                          name: "All stores",
                          id: "ALL_STORES",
                        },
                        ...storeList,
                      ]
                }
                idKey="id"
                nameKey="name"
                onChange={(value) => {
                  setCurrentStore(value);
                  filterByStoreId(value);
                }}
                value={
                  storeList?.find(
                    (storeItem) => `${storeItem?.id}` === `${currentStore}`,
                  )?.name || "All stores"
                }
                floatError={false}
                classNames="store-select"
              />
            </div>
            <primaryComponents.DateRangeInput
              startDate={startDate}
              endDate={endDate}
              onChange={setDateFilter}
            />
            <secondaryComponents.ToolTipV2
              tipText="Export Vat Ledger"
              position="left"
            >
              <primaryComponents.Button
                classNames={"btn btn--outline"}
                style={{ padding: "0 15px" }}
                onClick={() => setIsVatLogsExport(true)}
              >
                <DownloadStatementIcon />
              </primaryComponents.Button>
            </secondaryComponents.ToolTipV2>
          </div>
          <div className={styles["inventory-vatlogs__cards"]}>
            <div className={styles["inventory-vatlogs__card"]}>
              <secondaryComponents.LoaderHelper
                classNames="mt-2"
                isLoading={isFetchingData}
              >
                <h5 className="mb-0">
                  {formatCurrency({
                    value: vatSummaryData?.outflow_vat,
                    currencyCode: currencyCode,
                  })}
                </h5>
              </secondaryComponents.LoaderHelper>
              <div className={styles["inventory-vatlogs__card__card_title"]}>
                <p>VAT Collected</p>
                <secondaryComponents.ToolTipV2
                  tipText="This is the total VAT you charged customers on your sales in the selected period. <br> Example: You sold goods worth ₦100,000 and charged ₦7,500 VAT. VAT Collected = ₦7,500."
                  position="bottom"
                >
                  <ToolTipIcon />
                </secondaryComponents.ToolTipV2>
              </div>
            </div>
            <div className={styles["inventory-vatlogs__card"]}>
              <secondaryComponents.LoaderHelper
                classNames="mt-2"
                isLoading={isFetchingData}
              >
                <h5 className="mb-0">
                  {formatCurrency({
                    value: vatSummaryData?.inflow_vat,
                    currencyCode: currencyCode,
                  })}
                </h5>
              </secondaryComponents.LoaderHelper>
              <div className={styles["inventory-vatlogs__card__card_title"]}>
                <p>VAT on Purchases</p>
                <secondaryComponents.ToolTipV2
                  tipText="This is the total VAT included in eligible business purchases and expenses (for example: stock, inventory, raw materials, equipment, rent, utilities, and services) in the selected period. This is not VAT paid to FIRS.
<br>Example: You bought stock for ₦50,000 and paid ₦3,750 VAT. VAT on Purchases = ₦3,750."
                  position="bottom"
                >
                  <ToolTipIcon />
                </secondaryComponents.ToolTipV2>
              </div>
            </div>
            <div className={styles["inventory-vatlogs__card"]}>
              <secondaryComponents.LoaderHelper
                classNames="mt-2"
                isLoading={isFetchingData}
              >
                <h5 className="mb-0">
                  {formatCurrency({
                    value: vatSummaryData?.payable_amount,
                    currencyCode: currencyCode,
                  })}
                </h5>
              </secondaryComponents.LoaderHelper>
              <div className={styles["inventory-vatlogs__card__card_title"]}>
                <p>VAT Payable to FIRS</p>
                <secondaryComponents.ToolTipV2
                  tipText="This is calculated as VAT Collected minus VAT on Purchases. If the result is more than 0, that amount is payable to FIRS. If not, it is ₦0.00.
<br>Example: VAT Collected ₦7,500 − VAT on Purchases ₦3,750 = VAT Payable ₦3,750."
                  position="bottom"
                >
                  <ToolTipIcon />
                </secondaryComponents.ToolTipV2>
              </div>
            </div>
            <div className={styles["inventory-vatlogs__card"]}>
              <secondaryComponents.LoaderHelper
                classNames="mt-2"
                isLoading={isFetchingData}
              >
                <h5
                  className={`mb-0 ${vatSummaryData?.credit_amount && "text-danger"}`}
                >
                  {formatCurrency({
                    value: vatSummaryData?.credit_amount,
                    currencyCode: storeList?.find(
                      (storeItem) => `${storeItem?.id}` === `${currentStore}`,
                    )?.currency_code,
                  })}
                </h5>
                <div className={styles["inventory-vatlogs__card__card_title"]}>
                  <p>VAT Credit</p>
                  <secondaryComponents.ToolTipV2
                    tipText="This is calculated as VAT Collected minus VAT on Purchases. If the result is less than 0, the difference becomes VAT credit (shown as a positive amount). If not, it is ₦0.00.
<br>Example: VAT Collected ₦3,000 − VAT on Purchases ₦5,000 = VAT Credit ₦2,000."
                    position="bottom"
                  >
                    <ToolTipIcon />
                  </secondaryComponents.ToolTipV2>
                </div>
              </secondaryComponents.LoaderHelper>
            </div>
          </div>
          <div>
            <div className={`${globalStyles.rowNavigations} mb-4`}>
              {tableTab?.map((item, index) => {
                const key = item?.key;
                return (
                  <React.Fragment key={index}>
                    <secondaryComponents.NavigateWithPermission
                      permissionParentKey="Inventory Management"
                      showOnLocalSetup={item?.showOnLocalSetup}
                    >
                      {/* eslint-disable-next-line */}
                      <a
                        className={`${globalStyles.rowNavigations__item} ${
                          key === selectedTableTab ? globalStyles.active : ""
                        }`}
                        onClick={() => {
                          setSelectedTableTab(key);
                        }}
                      >
                        {item?.label}
                      </a>
                    </secondaryComponents.NavigateWithPermission>
                  </React.Fragment>
                );
              })}
            </div>
            <div className={globalStyles.tableWrapper}>
              <secondaryComponents.LoaderHelper
                isLoading={isLoadingVatInflowData || isLoadingvatOutflowData}
                classNames={"pt-5"}
              >
                {selectedTableTab === "vat_inflow" ? (
                  <secondaryComponents.VatInflowsTable
                    metaDetails={vatInflowMetaData}
                    vatInflowData={vatInflowData}
                    gotoPage={gotoPage}
                    selectedTab={selectedTableTab}
                  />
                ) : (
                  <secondaryComponents.VatOutflowsTable
                    vatOutflowData={vatOutflowData}
                    gotoPage={gotoPage}
                    metaDetails={vatOutflowMetaData}
                    selectedTab={selectedTableTab}
                  />
                )}
              </secondaryComponents.LoaderHelper>
              <secondaryComponents.SideModal
                isActive={isVatLogsExport}
                closeModal={() => setIsVatLogsExport(false)}
              >
                <secondaryComponents.VatLogsExport
                  closeWidget={() => setIsVatLogsExport(false)}
                  selectStartDate={startDate}
                  selectedEndDate={endDate}
                />
              </secondaryComponents.SideModal>
            </div>
          </div>
        </motion.div>
      </secondaryComponents.LoaderHelper>
    </secondaryComponents.MetaSetter>
  );
};

export default VatLogsPage;
